import { useEffect, useMemo, useRef, useState } from "react";
import { Share2, Download, Loader2, Trophy, Globe, X } from "lucide-react";
import { toast } from "sonner";
import { avatarSrc, web, errMsg } from "../../lib/api";
import { useApi } from "../../hooks/useApi";
import { shareImage } from "../../lib/shareImage";
import { PoweredByMark } from "./PoweredBy";
import { eventsOf, eventDate } from "../../pages/coach/Overview";
import { Btn } from "./kit";
import { Modal, Field, TextInput } from "./Form";

const SPOTS = ["PG", "SG", "SF", "PF", "C"];
const initials = (n = "") => n.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
const fmtWhen = (iso) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })} \u00b7 ${d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}`;
};
const opponentOf = (e) => e?.opponent ?? (e?.title ?? "").replace(/^(vs\.?|@)\s*/i, "");

/** Next upcoming game on this squad's schedule (Varsity unless JV is selected). */
const useNextGame = (tid, squad) => {
  const sched = useApi(tid && `/teams/${tid}/schedule`);
  const levels = useApi(tid && `/teams/${tid}/schedule/levels`, web);
  return useMemo(() => {
    const lmap = levels.data && typeof levels.data === "object" ? levels.data : {};
    const now = Date.now() - 3 * 36e5;
    return eventsOf(sched.data)
      .filter((e) => (e.type ?? "").toLowerCase() === "game" && new Date(eventDate(e)).getTime() > now && (lmap[e.id] === "jv" ? "jv" : "varsity") === squad)
      .sort((a, b) => new Date(eventDate(a)) - new Date(eventDate(b)))[0] ?? null;
  }, [sched.data, levels.data, squad]);
};

const Photo = ({ tid, p }) => {
  const [ok, setOk] = useState(true);
  return (
    <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-full border-2 border-[#39FF14]/70 bg-[#162032] font-display text-base font-black">
      {ok ? <img src={avatarSrc(tid, { ...p, has_app_photo: true })} alt="" crossOrigin="anonymous" onError={() => setOk(false)} className="h-full w-full object-cover" /> : initials(p.name)}
    </div>
  );
};

/** 360x450 card, exported at 3x = 1080x1350 (Instagram portrait). */
export const StartingFiveCard = ({ cardRef, tid, starters, teamName, squadLabel, opponent, when, where }) => (
  <div ref={cardRef} data-testid="starting-five-card" className="relative h-[450px] w-[360px] shrink-0 overflow-hidden bg-[#0A0E1A] p-6 text-white">
    <div aria-hidden className="absolute -right-6 -top-10 font-display text-[190px] font-black leading-none text-[#39FF14]/[0.06]">5</div>
    <div aria-hidden className="absolute inset-x-0 bottom-0 h-2 bg-[#39FF14]" />
    <div className="relative flex h-full flex-col">
      <p className="font-display text-sm font-bold tracking-wide">GAME<span className="text-[#FF3B30]">DAY</span> <span className="font-mono-gd text-[8px] tracking-[0.3em] text-[#39FF14]">COURTSIDE BY HOOPSHQ</span></p>
      <p className="mt-4 font-mono-gd text-[10px] uppercase tracking-[0.3em] text-[#39FF14]">Game day · Starting five</p>
      <p data-testid="starting-five-team" className="font-display text-2xl font-black uppercase leading-none">{teamName}{squadLabel ? <span className="text-[#39FF14]"> {squadLabel}</span> : null}</p>
      {(opponent || when) && (
        <p data-testid="starting-five-game" className="mt-1.5 font-mono-gd text-[10px] uppercase tracking-[0.15em] text-slate-200">
          {opponent ? `vs ${opponent}` : ""}{opponent && when ? " \u00b7 " : ""}{when}{where ? ` \u00b7 ${where}` : ""}
        </p>
      )}
      <ol className="mt-4 space-y-2">
        {SPOTS.map((pos) => {
          const p = starters[pos];
          return (
            <li key={pos} className="flex items-center gap-3 rounded-xl bg-[#0F172A] px-3 py-1.5 ring-1 ring-[#1E293B]">
              <span className="w-8 font-display text-xl font-black text-[#39FF14]">{pos}</span>
              {p ? <Photo tid={tid} p={p} /> : <div className="h-12 w-12 shrink-0 rounded-full border-2 border-dashed border-[#1E293B]" />}
              <span className="min-w-0 flex-1 truncate font-display text-lg font-black uppercase leading-none">{p?.name ?? "TBD"}</span>
              {p?.captain && <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#39FF14] font-display text-[11px] font-black text-[#0A0E1A]">C</span>}
              {p?.jersey != null && <span className="font-mono-gd text-xs text-slate-300">#{p.jersey}</span>}
            </li>
          );
        })}
      </ol>
      <div className="mt-auto flex items-end justify-between gap-2 pb-2">
        <p className="font-mono-gd text-[8px] uppercase tracking-[0.25em] text-slate-400">C = team captain</p>
        <PoweredByMark testid="starting-five-powered" />
      </div>
    </div>
  </div>
);

const FanPost = ({ tid, starters, squad, g, next }) => {
  const status = useApi(tid && `/teams/${tid}/public-lineup`, web);
  const [busy, setBusy] = useState(false);
  const posted = status.data?.lineup;
  const act = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); status.reload(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const post = () => act(() => web.post(`/teams/${tid}/public-lineup`, {
    level: squad, opponent: g.opponent.trim(), when: g.when.trim(), where: g.where.trim(), game_at: next ? eventDate(next) : null,
    starters: SPOTS.map((pos) => ({ pos, id: starters[pos]?.id ?? null })),
  }), "Starting five posted to your fan page");
  if (status.loading) return null;
  if (status.data && !status.data.page_enabled) {
    return <p data-testid="starting-five-fan-off" className="mr-auto self-center text-xs text-slate-400">Turn on your public fan page to post this lineup there.</p>;
  }
  return (
    <div className="mr-auto flex flex-wrap items-center gap-2">
      <Btn variant="ghost" data-testid="starting-five-post" disabled={busy} onClick={post}><Globe className="h-4 w-4" />{posted ? "Update fan page" : "Post to fan page"}</Btn>
      {posted && <Btn variant="ghost" data-testid="starting-five-unpost" disabled={busy} onClick={() => act(() => web.delete(`/teams/${tid}/public-lineup`), "Removed from your fan page")}><X className="h-4 w-4" />Remove</Btn>}
      {posted && <span data-testid="starting-five-posted" className="font-mono-gd text-[11px] uppercase tracking-wider text-[#39FF14]">On fan page{posted.opponent ? ` · vs ${posted.opponent}` : ""}</span>}
    </div>
  );
};

export const StartingFive = ({ tid, starters, teamName, level }) => {
  const squad = level === "jv" ? "jv" : "varsity";
  const squadLabel = level === "jv" ? (/\bjv\b/i.test(teamName ?? "") ? "" : "JV") : level === "varsity" ? "Varsity" : "";
  const next = useNextGame(tid, squad);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [g, setG] = useState({ opponent: "", when: "", where: "" });
  const ref = useRef(null);
  useEffect(() => {
    if (open) setG({ opponent: opponentOf(next), when: next ? fmtWhen(eventDate(next)) : "", where: next?.location ?? "" });
  }, [open, next]);
  const set = (k) => (e) => setG((s) => ({ ...s, [k]: e.target.value }));
  const filled = SPOTS.filter((s) => starters[s]).length;
  const run = async (mode) => {
    setBusy(true);
    try {
      const caption = `Game day! Our starting five${g.opponent ? ` vs ${g.opponent}` : ""}. #GAMEDAY`;
      const how = await shareImage(ref.current, { fileName: `${(teamName ?? "team").replace(/[^a-z0-9]+/gi, "-")}-starting-five.png`, title: "Starting Five", caption, mode });
      if (how === "downloaded") toast.success(mode === "share" ? "Image downloaded. Post it from your photos" : "Image downloaded");
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Couldn't create the image. Please try again");
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <Btn data-testid="starting-five-button" onClick={() => setOpen(true)}><Trophy className="h-4 w-4" />Starting five</Btn>
      <Modal open={open} onClose={() => setOpen(false)} testid="starting-five-dialog" title="Share your starting five"
        description={`The top player in each position on your depth chart${filled < 5 ? ` (${5 - filled} spot${5 - filled === 1 ? "" : "s"} empty, shown as TBD)` : ""}. Sized for Instagram, Facebook and X.`}>
        <div className="mt-3 grid gap-3 sm:grid-cols-3">
          <Field label="Opponent"><TextInput data-testid="starting-five-opponent" value={g.opponent} maxLength={60} onChange={set("opponent")} placeholder="Opponent" /></Field>
          <Field label="Date and time"><TextInput data-testid="starting-five-when" value={g.when} maxLength={40} onChange={set("when")} placeholder="Fri, Nov 7 · 7:00 PM" /></Field>
          <Field label="Location"><TextInput data-testid="starting-five-where" value={g.where} maxLength={60} onChange={set("where")} placeholder="Home gym" /></Field>
        </div>
        {!next && <p data-testid="starting-five-no-game" className="mt-2 text-xs text-slate-400">No upcoming {squad === "jv" ? "JV" : "Varsity"} game on the schedule. Type the details in.</p>}
        <div className="mt-3 flex h-[375px] justify-center overflow-hidden rounded-xl border border-[#1E293B] sm:h-[452px]">
          <div className="origin-top scale-[0.83] sm:scale-100">
            <StartingFiveCard cardRef={ref} tid={tid} starters={starters} teamName={teamName} squadLabel={squadLabel} opponent={g.opponent.trim()} when={g.when.trim()} where={g.where.trim()} />
          </div>
        </div>
        <div className="mt-4 flex flex-wrap justify-end gap-2">
          {open && <FanPost tid={tid} starters={starters} squad={squad} g={g} next={next} />}
          <Btn variant="ghost" data-testid="starting-five-download" disabled={busy} onClick={() => run("download")}><Download className="h-4 w-4" />Download</Btn>
          <Btn data-testid="starting-five-share" disabled={busy} onClick={() => run("share")}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Share2 className="h-4 w-4" />}Share</Btn>
        </div>
      </Modal>
    </>
  );
};
